import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { archivePblNotification, getPblNotifications, markPblNotificationRead, unarchivePblNotification } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import { Button } from '@/components/UI/Button';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import type { PblNotification } from '@/types/pbl';
import { formatPblDisplayDate } from '@/utils/pblDate';
import { errorHandler } from '@/utils/toastHandler';

import {
  ActionCell,
  ActionButton,
  FilterBar,
  FilterButton,
  NotificationTable,
  NotificationTableCard,
  NotificationTableScroll,
  NotificationsShell,
  PageButton,
  PaginationGroup,
  TableFooter,
  TitleBar,
} from './styles';
import { useTranslation } from 'react-i18next';

type NotificationFilter = 'all' | 'sent' | 'read' | 'archived';

const rowsPerPage = 10;

const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);

const buildNotificationTarget = (notification: PblNotification) => {
  if (notification.type === 'lawyer_cv_submitted' && notification.pbl_case_id == null
    && notification.cv_review_action_url === PATH.pbl.vettingCvRevisions) {
    return PATH.pbl.vettingCvRevisions;
  }
  const target = notification.case_action_url?.trim();
  return notification.can_open_case === true && target ? target : null;
};

const getNotificationActionLabel = (notification: PblNotification, t: (key: string) => string) =>
  notification.type === 'lawyer_cv_submitted' && notification.cv_review_action_url === PATH.pbl.vettingCvRevisions
    ? t('pbl.backlogEnhancements.cvReview.queueTitle')
    : notification.pbl_case_id
    ? t('pbl.pagesHardcoded.notifications.openCase')
    : t('pbl.pagesHardcoded.notifications.openDetails');

const isUnreadNotification = (notification: PblNotification) => notification.status === 'sent';

const PblNotifications = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useResize();
  const loadRequestIdRef = useRef(0);
  const [notifications, setNotifications] = useState<PblNotification[]>([]);
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const filterOptions = useMemo<Array<{ value: NotificationFilter; label: string }>>(
    () => [
      { value: 'all', label: t('pbl.pagesHardcoded.notifications.all') },
      { value: 'sent', label: t('pbl.pagesHardcoded.notifications.unread') },
      { value: 'read', label: t('pbl.pagesHardcoded.notifications.read') },
      { value: 'archived', label: t('pbl.pagesHardcoded.notifications.archived') },
    ],
    [t]
  );

  const applyNotificationPage = useCallback((page: { data: PblNotification[]; total: number; last_page: number }) => {
    setNotifications(page.data);
    setTotalEntries(page.total);
    setLastPage(page.last_page || 1);
  }, []);

  const loadNotifications = useCallback(async () => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;

    try {
      const params: NonNullable<Parameters<typeof getPblNotifications>[0]> = {
        channel: 'in_app',
        ...(filter === 'all' ? {} : { status: filter }),
        ...(filter === 'archived' ? { archived: true } : {}),
        per_page: rowsPerPage,
        page: currentPage,
      };
      const response = await getPblNotifications(params);

      if (response.data.success === false) {
        if (loadRequestIdRef.current !== requestId) return;
        errorHandler(response.data.message || t('pbl.pagesHardcoded.notifications.failedToLoadPblNotifications'));
        return;
      }

      if (loadRequestIdRef.current !== requestId) return;

      applyNotificationPage(response.data.data);
    } catch (error: any) {
      if (loadRequestIdRef.current !== requestId) return;
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.notifications.failedToLoadPblNotifications'));
    }
  }, [applyNotificationPage, currentPage, filter, t]);

  useEffect(() => {
    loadNotifications();

    return () => {
      loadRequestIdRef.current += 1;
    };
  }, [loadNotifications]);

  const changeFilter = (nextFilter: NotificationFilter) => {
    setFilter(nextFilter);
    setCurrentPage(1);
  };

  const openNotification = async (notification: PblNotification) => {
    const target = buildNotificationTarget(notification);

    if (!target) return;

    if (isUnreadNotification(notification)) {
      try {
        const response = await markPblNotificationRead(notification.id);
        if (response.data.success !== false) {
          setNotifications((items) => items.map((item) => item.id === notification.id ? { ...item, status: 'read' } : item));
        }
      } catch (error: any) {
        errorHandler(error.response?.data?.message || t('pbl.pagesHardcoded.notifications.failedToLoadPblNotifications'));
        return;
      }
    }

    if (isAbsoluteUrl(target)) {
      window.open(target, '_self', 'noopener,noreferrer');
      return;
    }

    navigate(target);
  };

  const toggleArchive = async (notification: PblNotification) => {
    try {
      const archived = notification.archived_at != null || notification.status === 'archived';
      const response = archived
        ? await unarchivePblNotification(notification.id)
        : await archivePblNotification(notification.id);
      if (response.data.success === false) return;
      setNotifications((items) => items.filter((item) => item.id !== notification.id));
      setTotalEntries((total) => Math.max(0, total - 1));
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('pbl.pagesHardcoded.notifications.failedToUpdateNotification'));
    }
  };

  const firstVisibleRow = notifications.length ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const lastVisibleRow = notifications.length ? Math.min(firstVisibleRow + notifications.length - 1, totalEntries) : 0;
  const pageItems = useMemo(() => Array.from({ length: Math.max(1, lastPage) }, (_, index) => index + 1), [lastPage]);

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button type="button" size="md" variant="primary" rounded={false} onClick={() => navigate(PATH.pbl.cases)}>
            {t('pbl.pagesHardcoded.notifications.back')}</Button>
          <span>
            {t('pbl.pagesHardcoded.notifications.home')}<strong>{t('pbl.pagesHardcoded.notifications.pblNotifications')}</strong>
          </span>
        </TitleBar>
      }
    >
      <NotificationsShell>
        <FilterBar>
          {filterOptions.map((item) => (
            <FilterButton
              key={item.value}
              type="button"
              active={filter === item.value}
              onClick={() => changeFilter(item.value)}
            >
              {item.label}
            </FilterButton>
          ))}
        </FilterBar>

        <NotificationTableCard>
          <NotificationTableScroll>
            <NotificationTable>
              <thead>
                <tr>
                  <th>{t('pbl.pagesHardcoded.notifications.subject')}</th>
                  <th>{t('pbl.pagesHardcoded.notifications.message')}</th>
                  <th>{t('pbl.pagesHardcoded.notifications.created')}</th>
                  <th>{t('pbl.pagesHardcoded.notifications.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification) => (
                  <tr key={notification.id} data-testid={`pbl-notification-${notification.id}`} data-unread={isUnreadNotification(notification)}>
                    <td data-label={t('pbl.pagesHardcoded.notifications.subject')}>{notification.subject}</td>
                    <td data-label={t('pbl.pagesHardcoded.notifications.message')}>{notification.body ?? '-'}</td>
                    <td data-label={t('pbl.pagesHardcoded.notifications.created')}>
                      {notification.created_at_display ?? formatPblDisplayDate(notification.created_at)}
                    </td>
                    <td data-label={t('pbl.pagesHardcoded.notifications.actions')}>
                      <ActionCell>
                        {buildNotificationTarget(notification) ? (
                          <ActionButton type="button" onClick={() => openNotification(notification)}>
                            {getNotificationActionLabel(notification, t)}
                          </ActionButton>
                        ) : null}
                        <ActionButton type="button" onClick={() => toggleArchive(notification)}>
                          {notification.archived_at != null || notification.status === 'archived'
                            ? t('pbl.pagesHardcoded.notifications.unarchive')
                            : t('pbl.pagesHardcoded.notifications.archive')}
                        </ActionButton>
                      </ActionCell>
                    </td>
                  </tr>
                ))}
                {notifications.length === 0 ? (
                  <tr>
                    <td colSpan={4}>{t('pbl.pagesHardcoded.notifications.noPblNotificationsFound')}</td>
                  </tr>
                ) : null}
              </tbody>
            </NotificationTable>
          </NotificationTableScroll>

          <TableFooter isMobile={isMobile}>
            <span>
              {t('pbl.pagesHardcoded.notifications.showing')}{firstVisibleRow} to {lastVisibleRow} of {totalEntries} entries
            </span>
            <PaginationGroup>
              {pageItems.map((page) => (
                <PageButton
                  key={page}
                  type="button"
                  active={page === currentPage}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </PageButton>
              ))}
            </PaginationGroup>
          </TableFooter>
        </NotificationTableCard>
      </NotificationsShell>
    </PageContainer>
  );
};

export default PblNotifications;
